import { useCallback, useRef, useEffect } from 'react';
import { useHistory } from '../use-history';
import { useStore } from '../../store';
import { useTrackingManager } from '../use-tracking-manager';
import { onVWBasicPageLoadEvent } from '../../tagging/tagging-helpers';
import { loadInitialData } from '../../store/load-initial-data';
import { useApolloClient } from '@apollo/client';
import {
  resetQsgData,
  setNotificationMessage,
  setQsgSearchTerm,
  setQsgSearchPage,
  setSelectedQsgModel,
} from '../../store/slices/qsg/actions';
import { useLogger } from '../use-logger';
import {
  isGuideRoute,
  getModelPathParams,
  getQueryParams,
  getSearchTermFromQuery,
  getSearchPageFromQuery,
  getModelIdFromHistory,
  getModelYearFromHistory,
  getSectionFromPath,
} from '../../utils/query-param-helpers';
import { redirectToDefaultSection } from '../../store/load-initial-data';
import { findFirstIndexableSection, findSectionRecursive, isSectionIndexable } from '../../utils/navigation';

interface UseInitialDataProps {
  setHasInit: (v: boolean) => void;
}

export const useInitialData = ({ setHasInit }: UseInitialDataProps) => {
  const [state, dispatch] = useStore();
  const history = useHistory();
  const trackingManager = useTrackingManager();
  const client = useApolloClient();
  const logger = useLogger();
  const hasLoadedRef = useRef(false);

  const prevModelIdRef = useRef<string>('');
  const prevModelYearRef = useRef<string>('');
  const prevSectionRef = useRef<string>('');

  const loadInitialDataCallback = useCallback(
    async (isReloading: boolean = false) => {
      // Skip if already loaded (e.g., from SSR) - unless we're forcing a reload
      if (hasLoadedRef.current && !isReloading) {
        logger.info('[Client] Initial data already loaded, skipping');
        return;
      }

      // Check if we already have data loaded (from SSR) - skip if we're not reloading
      if (!isReloading) {
        const hasData =
          state.qsg.availableModels.length > 0 ||
          state.qsg.qsgContent !== null ||
          state.qsg.qsgNavigation !== null;

        if (hasData) {
          logger.info(
            '[Client] Data already loaded from SSR, skipping client-side data load'
          );

          // In integration env, query params are not available on the server side (SSR)
          // Sync URL parameters to store before setting hasInit
          // This ensures the store matches the URL during hydration
          const query = getQueryParams(history);
          const isOnGuideRoute = isGuideRoute(history.location.pathname);
          const modelYear = getModelYearFromHistory(history);
          const modelId = getModelIdFromHistory(history);

          const loadQSG = Boolean(modelId && modelYear && isOnGuideRoute);
          const urlSearchTerm = getSearchTermFromQuery(query);
          const urlSearchPage = getSearchPageFromQuery(query);

          // Log hydration state for debugging (before sync)
          console.group('[HYDRATION] SSR Data Detected - Syncing URL to Store');
          console.log(
            'Current URL:',
            history.location.pathname + history.location.search
          );
          console.log('Store State (before sync):', {
            searchTerm: state.qsg.qsgSearchTerm,
            searchPage: state.qsg.qsgSearchPage,
            modelId: state.qsg.selectedModel.modelId,
            modelYear: state.qsg.selectedModel.modelYear,
          });
          console.log('URL Params:', {
            section: query.section,
            search: query.search,
            page: query.page,
            model: query.model,
            'model-year': query['model-year'],
            'guide-mode': query['guide-mode'],
          });

          if (modelId || modelYear) {
            const selectedModel = {
              modelId,
              modelYear,
            };

            dispatch(setSelectedQsgModel(selectedModel));
            logger.info(
              `[Client] Synced selected model from URL to store: "${selectedModel.modelId}/${selectedModel.modelYear}"`
            );
          }

          // Sync search term and page from URL to the store if we're on a guide route
          if (loadQSG) {
            if (urlSearchTerm !== state.qsg.qsgSearchTerm) {
              dispatch(setQsgSearchTerm(urlSearchTerm));
              logger.info(
                `[Client] Synced search term from URL to store: "${urlSearchTerm}"`
              );
            }

            if (urlSearchTerm && urlSearchPage !== state.qsg.qsgSearchPage) {
              dispatch(setQsgSearchPage(urlSearchPage));
              logger.info(
                `[Client] Synced search page from URL to store: ${urlSearchPage}`
              );
            } else if (!urlSearchTerm && state.qsg.qsgSearchPage !== 1) {
              dispatch(setQsgSearchPage(1));
              logger.info(
                `Resetting search page: ${state.qsg.qsgSearchPage} -> 1`
              );
            }
          }

          console.log('Initial client-side URL->Store sync complete');
          console.groupEnd();

          // Redirect to default section on desktop if SSR initialized the data
          if (loadQSG && state.qsg.qsgNavigation) {
            const currentSection = getSectionFromPath(history.location.pathname);
            const sections = state.qsg.qsgNavigation.sections ?? [];
            const foundSection = findSectionRecursive(sections, currentSection);
            const validFoundSection = foundSection && isSectionIndexable(foundSection) ? foundSection : null;
            const defaultSection = findFirstIndexableSection(sections);
            const isSearch = Boolean(urlSearchTerm);

            if (!validFoundSection)
              redirectToDefaultSection(
                history,
                defaultSection,
                isSearch,
                false,
                logger
              );
          }

          hasLoadedRef.current = true;
          setHasInit(true);
          return;
        }
      }

      logger.info('[Client] Starting initial data load (CSR)');
      onVWBasicPageLoadEvent(trackingManager);
      hasLoadedRef.current = true;

      try {
        await loadInitialData({
          client,
          history,
          currentState: state,
          dispatch,
          logger,
        });

        logger.info('[Client] Initial data loaded successfully (CSR)');
        setHasInit(true);
      } catch (error) {
        logger.error('[Client] Failed to load initial data:', error);
        history.push('/');
        dispatch(setNotificationMessage('Failed to load initial data!'));
        setHasInit(true);
      }
    },
    [client, history, state, dispatch, setHasInit, trackingManager, logger]
  );

  const onError = useCallback(
    (error: unknown) => {
      logger.error('[Client] Error loading initial data:', error);
      history.push('/');
      dispatch(setNotificationMessage('Failed to load initial data!'));
    },
    [dispatch, history, logger]
  );

  const onReset = useCallback(() => {
    dispatch(resetQsgData());
    hasLoadedRef.current = false;
  }, [dispatch]);

  // Watch for model or section changes (e.g., browser back/forward navigation)
  useEffect(() => {
    const isOnGuideRouteValue = isGuideRoute(history.location.pathname);
    if (!isOnGuideRouteValue) return;

    const pathParams = getModelPathParams(history.location.pathname);
    const urlModelId = pathParams.modelId;
    const urlModelYear = pathParams.modelYear;
    const urlSection = pathParams.section;

    const currentNavigation = state.qsg.qsgNavigation;

    // If model changed and we have navigation data for a different model, reload
    const modelChanged =
      urlModelId &&
      urlModelYear &&
      currentNavigation &&
      (currentNavigation.modelId !== urlModelId ||
        currentNavigation.modelYear !== urlModelYear);

    if (modelChanged) {
      logger.info(
        `[Client] Model changed in URL (${urlModelId}/${urlModelYear}), reloading data`
      );

      // Reset data and flag to allow reload
      dispatch(resetQsgData());
      hasLoadedRef.current = false;
      loadInitialDataCallback(true);
    }

    prevModelIdRef.current = urlModelId;
    prevModelYearRef.current = urlModelYear;
    prevSectionRef.current = urlSection;
  }, [
    history.location.pathname,
    state.qsg.selectedModel,
    state.qsg.qsgNavigation,
    loadInitialDataCallback,
    logger,
    dispatch,
  ]);

  return {
    loadInitialData: loadInitialDataCallback,
    onError,
    onReset,
  };
};
